import React, { useEffect, useState } from 'react';
import { utils, writeFile } from 'xlsx';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import '../../scss/vendors/bstyles.scss';
import Button from '../../components/elements/Button';
import { useMutation } from '@tanstack/react-query';
import { showNotification } from '../../utilities/mantineNotification';
import Card from '../../components/elements/Card';
import { Flex, Grid, Group, Stack, Text } from '@mantine/core';
import LoaderChart from './LoaderChart';
import {
  authorizedFilterBu,
  authorizedFilterDept,
  authorizedFilterSection,
  authorizedFilterSubBu,
  ChartTrainingProps
} from './ChartTraining';
import dayjs from 'dayjs';
import id from 'dayjs/locale/id';
import { dateFormat } from '../../utilities/dataFormat';
import * as Unicons from '@iconscout/react-unicons';
import { useDisclosure } from '@mantine/hooks';
import ChartFilter from './ChartFilter';
import ModalChart from '../../components/fragments/ModalChart';
import LineChart from '../../components/elements/LineChart';
import useLocalStorage from '../../hooks/useUserData';
import {
  downloadChartCompletionRateProgram,
  getChartCompletionRateProgram
} from '../../services/dashboardExecutive';
import ModalDownload from '../dashboard_bcld/ModalDownload';
import BarChart from '../../components/elements/BarChart';
import { HiChevronRight } from 'react-icons/hi';
import { Label } from 'reactstrap';

ChartJS.register(
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export interface DataCompletionRate {
  courseId: string;
  courseName: string;
  completionDate: string;
  enrolmentDate: string;
  enrolmentMethod: string;
  buName: null | string;
  subBuName: null | string;
  deptName: null | string;
  binusianId: null | string;
  name: null | string;
  buCode: null | string;
  subBuCode: null | string;
  deptCode: null | string;
  sectionCode: null | string;
  sectionName: null | string;
  subSectionCode: null | string;
  subSectionName: null | string;
  bandName: null | string;
}

const ChartCompletionRate: React.FC<ChartTrainingProps> = ({
  endDate,
  title,
  startDate,
  buCode,
  buName,
  subBuCode,
  subBuName,
  deptCode,
  deptName,
  sectionCode,
  sectionName,
  bandName
}) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [openedDownload, { toggle: toggleDownload }] = useDisclosure(false);
  const [statusFMSTR, setStatusFMSTR] = useState('0');
  const [chartFilter, setChartFilter] = useState({
    startDate: null,
    endDate: null,
    bulan: null,
    tahun: null,
    buName: null,
    subBuName: null,
    deptName: null,
    sectionName: null,
    buCode: null,
    subBuCode: null,
    deptCode: null,
    sectionCode: null,
    program: null
  });
  const [userData, setUserData] = useLocalStorage('persist:root', null);
  const data = JSON.parse(userData?.auth);
  const [chartModal, setChartModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chartLevel, setChartLevel] = useState(0);
  const [currentData, setCurrentData] = useState(null);
  const [chartConfig, setChartConfig] = useState([]);

  const maximumLevel = () => {
    if (bandName === 'Senior Manager') {
      return 4;
    }
    if (bandName === 'Manager') {
      return 3;
    }
    if (bandName === 'Supervisor') {
      return 4;
    }if (bandName === 'Director' || bandName === 'General Manager'){
      return 5
    }
    return 6;
  };
  const fetchedDataChart = useMutation({
    mutationFn: () =>
      getChartCompletionRateProgram({
        StartDate: chartFilter.startDate,
        EndDate: chartFilter.endDate,
        BinusianId: data?.userData?.binusianId,
        Month: chartFilter.bulan,
        Year: chartFilter.tahun,
        BuName: chartFilter.buName,
        SubBuName: chartFilter.subBuName,
        DeptName: chartFilter.deptName,
        SectionName: chartFilter.sectionName,
        BuCode: chartFilter.buCode,
        SubBuCode: chartFilter.subBuCode,
        DeptCode: chartFilter.deptCode,
        SectionCode: chartFilter.sectionCode,
        Program: chartFilter.program
      }),
    onError: (error: any) => {
      showNotification({
        type: 'error',
        message: error
      });
    }
  });

  useEffect(() => {
    setChartFilter((prevState) => ({
      ...prevState,
      startDate,
      endDate
    }));
  }, [endDate, startDate]);

  useEffect(() => {
    fetchedDataChart.mutate();
  }, [
    chartFilter?.buName,
    chartFilter?.bulan,
    chartFilter?.tahun,
    chartFilter?.subBuName,
    chartFilter?.startDate,
    chartFilter?.endDate,
    chartFilter?.deptName,
    chartFilter?.sectionName,
    chartFilter?.program
  ]);

  useEffect(() => {
    if (chartFilter && chartFilter.startDate && chartFilter.endDate) {
      const commonFields = {
        startDate: chartFilter.startDate,
        endDate: chartFilter.endDate,
        bulan: chartFilter.bulan,
        tahun: chartFilter.tahun,
        program: chartFilter.program,
        buName: null,
        subBuName: null,
        deptName: null,
        sectionName: null,
        buCode: null,
        subBuCode: null,
        deptCode: null,
        sectionCode: null
      };

      const levelConfig = (chartLevel) => {
        const authorizedBu = authorizedFilterBu.includes(bandName);
        const authorizedSubBu = authorizedFilterSubBu.includes(bandName);
        const authorizedDept = authorizedFilterDept.includes(bandName);
        const authorizedSection = authorizedFilterSection.includes(bandName);
        let level = [
          {},
          {},
          {
            buName: chartFilter.buName,
            buCode: chartFilter.buCode,
            subBuName: chartFilter.subBuName,
            subBuCode: chartFilter.subBuCode
          },
          {
            buName: chartFilter.buName,
            buCode: chartFilter.buCode,
            subBuName: chartFilter.subBuName,
            subBuCode: chartFilter.subBuCode
          },
          {
            buName: chartFilter.buName,
            buCode: chartFilter.buCode,
            subBuName: chartFilter.subBuName,
            subBuCode: chartFilter.subBuCode
          },
          {
            buName: chartFilter.buName,
            buCode: chartFilter.buCode,
            subBuName: chartFilter.subBuName,
            subBuCode: chartFilter.subBuCode,
            deptName: chartFilter.deptName,
            deptCode: chartFilter.deptCode
          },
          {
            buName: chartFilter.buName,
            buCode: chartFilter.buCode,
            subBuName: chartFilter.subBuName,
            subBuCode: chartFilter.subBuCode,
            deptName: chartFilter.deptName,
            deptCode: chartFilter.deptCode,
            sectionName: chartFilter.sectionName,
            sectionCode: chartFilter.sectionCode
          }
        ];
        if (
          !authorizedBu &&
          authorizedSubBu &&
          authorizedDept &&
          authorizedSection
        ) {
          level = [
            {},
            {},
            {
              subBuName: chartFilter.subBuName,
              subBuCode: chartFilter.subBuCode
            },
            {
              subBuName: chartFilter.subBuName,
              subBuCode: chartFilter.subBuCode,
              deptName: chartFilter.deptName,
              deptCode: chartFilter.deptCode
            },
            {
              subBuName: chartFilter.subBuName,
              subBuCode: chartFilter.subBuCode,
              deptName: chartFilter.deptName,
              deptCode: chartFilter.deptCode,
              sectionName: chartFilter.sectionName,
              sectionCode: chartFilter.sectionCode
            }
          ];
        }
        if (
          !authorizedBu &&
          !authorizedSubBu &&
          authorizedDept &&
          authorizedSection
        ) {
          level = [
            {},
            {},
            {
              deptName: chartFilter.deptName,
              deptCode: chartFilter.deptCode
            },
            {
              deptName: chartFilter.deptName,
              deptCode: chartFilter.deptCode,
              sectionName: chartFilter.sectionName,
              sectionCode: chartFilter.sectionCode
            }
          ];
        }
        if (
          !authorizedBu &&
          !authorizedSubBu &&
          !authorizedDept &&
          authorizedSection
        ) {
          level = [
            {},
            {},
            {
              sectionName: chartFilter.sectionName,
              sectionCode: chartFilter.sectionCode
            }
          ];
        }
        return level[chartLevel];
      };

      if (
        chartLevel >= 1 &&
        chartLevel <= maximumLevel() &&
        chartConfig.length === chartLevel - 1
      ) {
        setChartConfig((prevState) => [
          ...prevState,
          {
            ...commonFields,
            buName: chartFilter.buName,
            buCode: chartFilter.buCode,
            subBuName: chartFilter.subBuName,
            subBuCode: chartFilter.subBuCode,
            deptName: chartFilter.deptName,
            deptCode: chartFilter.deptCode,
            sectionName: chartFilter.sectionName,
            sectionCode: chartFilter.sectionCode
          }
        ]);
      }
    }
  }, [chartFilter, chartLevel, chartConfig.length]);

  const onClick = (element) => {
    if (element.length > 0) {
      if (chartLevel === 0) {
        setChartModal(true);
      }
      if (chartModal) {
        if (chartLevel < maximumLevel()) {
          setChartLevel((prevLevel) => {
            return prevLevel + 1;
          });
        }
        const index = element[0].index;
        // Get the label of the clicked bar
        const item = fetchedDataChart?.data?.data?.data[index];
        if (chartLevel < maximumLevel()) {
          const buLabel = () => {
            if(authorizedFilterBu.includes(bandName) && chartLevel === 2){
              return { label: item?.label, code : item?.code}
            }
            if ((bandName === 'Managing Director') && chartLevel === 2) {
              return { label: item?.label, code: item?.code };
            }

            return null;
          };

          const subBuLabel = () => {
            if (
              authorizedFilterSubBu.includes(bandName) &&
              chartLevel === 2 &&
              !(authorizedFilterBu.includes(bandName) || bandName === 'Managing Director')
            ) {
              return { label: item?.label, code: item?.code };
            }

            if(authorizedFilterBu.includes(bandName) && chartLevel === 2){
              return { label: item?.label, code : item?.code}
            }

            if (( bandName === 'Managing Director') && chartLevel === 3) {
              return { label: item?.label, code: item?.code };
            }

            return null;
          };

          const deptLabel = () => {
            if (
              authorizedFilterDept.includes(bandName) &&
              chartLevel === 2 &&
              !authorizedFilterSubBu.includes(bandName) &&
              !(authorizedFilterBu.includes(bandName) || bandName === 'Managing Director')
            ) {
              return { label: item?.label, code: item?.code };
            }

            if (
              authorizedFilterSubBu.includes(bandName) &&
              chartLevel === 3 &&
              !(authorizedFilterBu.includes(bandName) || bandName === 'Managing Director')
            ) {
              return { label: item?.label, code: item?.code };
            }

            if(authorizedFilterBu.includes(bandName) && chartLevel === 3){
              return { label: item?.label, code : item?.code}
            }

            if ((bandName === 'Managing Director') && chartLevel === 4) {
              return { label: item?.label, code: item?.code };
            }

            return null;
          };

          const sectionLabel = () => {
            if (
              authorizedFilterSection.includes(bandName) &&
              chartLevel === 2 &&
              !(authorizedFilterBu.includes(bandName) || bandName === 'Managing Director')&&
              !authorizedFilterSubBu.includes(bandName) &&
              !authorizedFilterDept.includes(bandName)
            ) {
              return { label: item?.label, code: item?.code };
            }

            if (
              authorizedFilterDept.includes(bandName) &&
              chartLevel === 3 &&
              !(authorizedFilterBu.includes(bandName) || bandName === 'Managing Director') &&
              !authorizedFilterSubBu.includes(bandName)
            ) {
              return { label: item?.label, code: item?.code };
            }

            if (
              authorizedFilterSubBu.includes(bandName) &&
              chartLevel === 4 &&
              !(authorizedFilterBu.includes(bandName) || bandName === 'Managing Director')
            ) {
              return { label: item?.label, code: item?.code };
            }

            if(authorizedFilterBu.includes(bandName) && chartLevel === 4){
              return { label: item?.label, code : item?.code}
            }

            if ((bandName === 'Managing Director') && chartLevel === 5) {
              return { label: item?.label, code: item?.code };
            }

            return null;
          };

          setChartFilter((prevState) => ({
            ...prevState,
            bulan: item.month.toString(),
            tahun: item.year.toString(),
            program: chartLevel === 1 ? item.label : prevState.program,
            buName: bandName === 'Managing Director' ? buLabel()?.label || prevState.buName : buName ? buName : prevState.buName,
            subBuName: bandName === 'Director' || bandName === 'General Manager' ||  bandName === 'Managing Director' ? subBuLabel()?.label || prevState.subBuName : subBuName ? subBuName : prevState.subBuName,
            deptName: bandName === 'Senior Manager' || bandName === 'Managing Director' || bandName === 'Director' || bandName === 'General Manager' ? deptLabel()?.label || prevState.deptName : deptName ? deptName : prevState.deptName ,
            sectionName: bandName === 'Manager' || bandName === 'Managing Director' || bandName === 'Director' || bandName === 'General Manager' || bandName === 'Senior Manager'? sectionLabel()?.label || prevState.sectionName : sectionName ? sectionName : prevState.sectionName,
            buCode: bandName === 'Managing Director' ? buLabel()?.code || prevState.buCode : buCode ? buCode : prevState.buCode,
            subBuCode: bandName === 'Director' || bandName === 'General Manager' || bandName === 'Managing Director' ? subBuLabel()?.code || prevState.subBuCode : subBuCode ? subBuCode : prevState.subBuCode,
            deptCode: bandName === 'Senior Manager' || bandName === 'Managing Director' || bandName === 'Director' || bandName === 'General Manager' ? deptLabel()?.code || prevState.deptCode : deptCode ? deptCode : prevState.deptCode,
            sectionCode: bandName === 'Manager' || bandName === 'Managing Director' || bandName === 'Director' || bandName === 'General Manager' || bandName === 'Senior Manager' ? sectionLabel()?.code || prevState.sectionCode : sectionCode ? sectionCode : prevState.sectionCode
            // buName : buLabel()?.label || prevState.buName,
            // subBuName : subBuLabel()?.label || prevState.subBuName,
            // deptName : deptLabel()?.label || prevState.deptName,
            // sectionName : sectionLabel()?.label || prevState.sectionName,
            // buCode : buLabel()?.code || prevState.buCode,
            // subBuCode : subBuLabel()?.code || prevState.subBuCode,
            // deptCode : deptLabel()?.code || prevState.deptCode,
            // sectionCode : sectionLabel()?.code || prevState.sectionCode
          }));
        }
      }
    }
  };

  const handleCloseModal = () => {
    setChartFilter((prevState) => ({
      ...prevState,
      bulan: null,
      tahun: null,
      buName: null,
      subBuName: null,
      deptName: null,
      sectionName: null,
      buCode: null,
      subBuCode: null,
      deptCode: null,
      sectionCode: null,
      program: null
    }));

    setLoading(true);

    setTimeout(() => {
      setChartLevel(0);
      setChartConfig([]);
      setChartModal(false);
      setLoading(false);
      fetchedDataChart.mutate();
    }, 500);
  };

  useEffect(() => {
    setChartFilter((prevState) => ({
      ...prevState,
      bulan: chartConfig[chartLevel - 1]?.bulan,
      tahun: chartConfig[chartLevel - 1]?.tahun,
      buName: chartConfig[chartLevel - 1]?.buName,
      subBuName: chartConfig[chartLevel - 1]?.subBuName,
      buCode: chartConfig[chartLevel - 1]?.buCode,
      subBuCode: chartConfig[chartLevel - 1]?.subBuCode,
      deptCode: chartConfig[chartLevel - 1]?.deptCode,
      sectionCode: chartConfig[chartLevel - 1]?.sectionCode,
      deptName: chartConfig[chartLevel - 1]?.deptName,
      sectionName: chartConfig[chartLevel - 1]?.sectionName,
      program: chartConfig[chartLevel - 1]?.program
    }));
    setCurrentData(chartConfig[chartLevel - 1]);
  }, [chartConfig, chartLevel]);

  const handleBack = () => {
    setLoading(true);
    if (chartLevel - 1 === 0) {
      setChartFilter((prevState) => ({
        ...prevState,
        bulan: null,
        tahun: null,
        buName: null,
        subBuName: null,
        buCode: null,
        subBuCode: null,
        deptName: null,
        deptCode: null,
        sectionName: null,
        sectionCode: null,
        program: null
      }));
      fetchedDataChart.mutate();
    }
    setTimeout(() => {
      setChartLevel((prevState) => prevState - 1);
      setChartConfig((prevState) => prevState.slice(0, -1));
      setLoading(false);
    }, 500);
  };

  const handleDownload = () => fetchedDownloadChart.mutate();

  const fetchedDownloadChart = useMutation({
    mutationFn: () =>
      downloadChartCompletionRateProgram({
        StartDate: chartFilter.startDate,
        EndDate: chartFilter.endDate,
        Month: chartFilter.bulan,
        BinusianId: data?.userData?.binusianId,
        Year: chartFilter.tahun,
        BuName: bandName === 'Director' ? buName : chartFilter.buName,
        SubBuName: bandName === 'Senior Manager' ? subBuName : chartFilter.subBuName,
        DeptName: bandName === 'Manager' ? deptName : chartFilter.deptName,
        SectionName: bandName === 'Supervisor' ? sectionName : chartFilter.sectionName,
        BuCode: bandName === 'Director' ? buCode : chartFilter.buCode,
        SubBuCode: bandName === 'Senior Manager' ? subBuCode : chartFilter.subBuCode,
        DeptCode: bandName === 'Manager' ? deptCode : chartFilter.deptCode,
        SectionCode: bandName === 'Supervisor' ? sectionCode : chartFilter.sectionCode,
        Program: chartFilter.program,
        status: statusFMSTR
      }),
    onSuccess: (data) => {
      if (data?.data?.data?.length > 0) {
        showNotification({
          type: 'success',
          message: 'Download in progress, please wait a moment.'
        });
        renderConvertExcel(data?.data?.data);
        return;
      }

      showNotification({
        type: 'error',
        message: `Data download is empty. Please try again later.`
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        message: error
      });
    }
  });

  const renderConvertExcel = (data: DataCompletionRate[]) => {
    const formatFilename = () => {
      const parts = [
        `Completion Rate`,
        chartFilter.startDate,
        chartFilter.endDate,
        ` download at ${dayjs().toLocaleString()}`
      ];
      return parts.filter(Boolean).join('_');
    };

    const filename = formatFilename();
    const wb = utils.book_new();

    // Convert the JSON data to a worksheet
    const ws = utils.json_to_sheet(data);

    // Add the worksheet to the workbook
    utils.book_append_sheet(wb, ws, 'Sheet1');

    // Write the workbook to a file
    // This will trigger a file download in the browser
    writeFile(wb, `${filename}.xlsx`);
  };

  const disabledClickChart = () => {
    if (bandName === 'Supervisor') {
      if (chartLevel === 1) {
        return false;
      }
      if (!chartModal) {
        return true;
      } else {
        return true;
      }
    } else {
      return true;
    }
  };
  const renderContent = () => {
    return (
      <BarChart
        key={chartLevel.toString()}
        chartKeys={['rate']}
        chartLabels={['Rate']}
        chartColors={['#7B2183', '#75B843']}
        data={fetchedDataChart?.data?.data?.data || []}
        onClick={disabledClickChart() && onClick}
        loading={fetchedDataChart.isLoading}
        isError={fetchedDataChart.isError}
        onLoad={() => fetchedDataChart.mutate()}
        withLegend
      />
    );
  };

  const onDownload = () => toggleDownload();

  const handleConfirmDownload = (data: any) => {
    setStatusFMSTR(data.status);
    setTimeout(() => {
      toggleDownload();
      handleDownload();
    }, 500);
  };

  const handleBreadcrumb = (item) => {
    setLoading(true);
    setTimeout(() => {
      setChartLevel(item.level);
      setChartConfig((prevState) => prevState.slice(0, item.level));
      setLoading(false);
    }, 500);
  };

  const renderModalBreadcrumb = () => {
    if (chartLevel <= 0) return null;
    if (bandName === 'Supervisor') return null;
    const buNameLevel = () => {
      if (authorizedFilterBu.includes(bandName)) {
        return 2;
      }
      return null;
    };

    const subBuNameLevel = () => {
      if (
        authorizedFilterSubBu.includes(bandName) &&
        !authorizedFilterBu.includes(bandName)
      ) {
        return 2;
      }
      if (authorizedFilterBu.includes(bandName)) {
        return 3;
      }
      return null;
    };

    const deptNameLevel = () => {
      if (
        authorizedFilterDept.includes(bandName) &&
        !authorizedFilterSubBu.includes(bandName) &&
        !authorizedFilterBu.includes(bandName)
      ) {
        return 3;
      }
      if (
        authorizedFilterSubBu.includes(bandName) &&
        !authorizedFilterBu.includes(bandName)
      ) {
        return 4;
      }
      if (authorizedFilterBu.includes(bandName)) {
        return 5;
      }
      return null;
    };

    const sectionNameLevel = () => {
      if (
        authorizedFilterSection.includes(bandName) &&
        !authorizedFilterBu.includes(bandName) &&
        !authorizedFilterSubBu.includes(bandName) &&
        !authorizedFilterDept.includes(bandName)
      ) {
        return 3;
      }
      if (
        authorizedFilterDept.includes(bandName) &&
        !authorizedFilterBu.includes(bandName) &&
        !authorizedFilterSubBu.includes(bandName)
      ) {
        return 4;
      }
      if (
        authorizedFilterSubBu.includes(bandName) &&
        !authorizedFilterBu.includes(bandName)
      ) {
        return 5;
      }
      if (authorizedFilterBu.includes(bandName)) {
        return 6;
      }
      return null;
    };

    const breadcrumbData = [
      {
        title: currentData?.bulan
          ? dayjs()
              .month(currentData.bulan - 1)
              .locale(id)
              .format('MMMM')
          : '',
        level: 1
      },
      {
        title: currentData?.program || '',
        level: 2
      },
      {
        title: currentData?.buName || '',
        level: buNameLevel()
      },
      {
        title: currentData?.subBuName || '',
        level: subBuNameLevel()
      },
      {
        title: currentData?.deptName || '',
        level: deptNameLevel()
      },
      {
        title: currentData?.sectionName || '',
        level: sectionNameLevel()
      }
    ];

    const filteredItems = breadcrumbData.filter((item) => item.title);

    return (
      <Group gap={0}>
        {filteredItems.map((item, index) => (
          <React.Fragment key={index}>
            <Button
              onClick={() => handleBreadcrumb(item)}
              variant="transparent"
            >
              {item.title}
            </Button>
            {index !== filteredItems.length - 1 && <HiChevronRight />}
          </React.Fragment>
        ))}
      </Group>
    );
  };

  return (
    <>
      <Grid.Col span={{ base: 12, md: 4 }}>
        {fetchedDataChart.isLoading && <LoaderChart />}
        {!fetchedDataChart.isLoading && (
          <Card h={'100%'}>
            <Flex gap={16} justify={'space-between'}>
              <Stack gap={0}>
                <Text className="text-sm-semibold">{title}</Text>
                <Text className="text-xs-medium" c={'gray'}>
                  Period{' '}
                  {dayjs(chartFilter.startDate)
                    .locale(id)
                    .format(dateFormat('dateyear'))}{' '}
                  -{' '}
                  {dayjs(chartFilter.endDate)
                    .locale(id)
                    .format(dateFormat('dateyear'))}
                </Text>
              </Stack>

              <Stack>
                <Button
                  variant="outline"
                  size="sm"
                  styles={{
                    padding: '6px 10px',
                    borderColor: '#CCCCCC',
                    color: '#333333',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12
                  }}
                  onClick={() => open()}
                >
                  <Unicons.UilFilter size={14} />
                </Button>
              </Stack>
            </Flex>
            <Flex mt={16} flex={1} h={'100%'}>
              {renderContent()}
            </Flex>
          </Card>
        )}
      </Grid.Col>
      <ChartFilter
        isVisible={opened}
        onDownload={onDownload}
        loading={fetchedDownloadChart.isLoading}
        onClose={() => close()}
        downloadable
        endDate={chartFilter.endDate}
        startDate={chartFilter.startDate}
        onFilter={(data) =>
          setChartFilter((prevState) => ({ ...prevState, ...data }))
        }
      />

      <ModalChart
        title={title}
        bandName={bandName}
        description={renderModalBreadcrumb()}
        loading={loading}
        isVisible={chartModal}
        onClose={handleCloseModal}
        backButton={chartLevel > 0}
        onBack={handleBack}
        downloadable
        onDownload={onDownload}
        loadingDownload={fetchedDownloadChart.isLoading}
        chart={renderContent()}
      />

      <ModalDownload
        opened={openedDownload}
        title={`Download ${title}`}
        onClose={() => toggleDownload()}
        onConfirm={handleConfirmDownload}
      />
    </>
  );
};

export default ChartCompletionRate;
